import { supabase } from '../lib/supabase';

/**
 * State Park Service
 * Provides methods to fetch state park data from the database
 */
export const stateParkService = {
  /**
   * Get state park information by park name
   * @param {string} parkName - Name of the park to search for
   * @returns {Promise<Object|null>} Park data or null if not found
   */
  async getParkByName(parkName) {
    try {
      if (!parkName) return null;

      // Clean park name for better matching (remove common suffixes)
      const cleanName = parkName?.replace(/\s+(State Park|National Park|Park)$/i, '')?.trim();

      // Try exact match first
      let { data, error } = await supabase?.from('state_parks')?.select('*')?.ilike('name', `%${cleanName}%`)?.limit(1)?.single();

      // If not found, try with original name
      if (error || !data) {
        const result = await supabase?.from('state_parks')?.select('*')?.ilike('name', `%${parkName}%`)?.limit(1)?.single();
        
        data = result?.data;
        error = result?.error;
      }

      if (error || !data) {
        console.warn(`⚠️ Park not found in database: ${parkName}`);
        return null;
      }

      return {
        id: data?.id,
        name: data?.name,
        state: data?.state,
        parkType: data?.park_type,
        description: data?.description,
        websiteUrl: data?.website_url,
        totalAcreage: data?.total_acreage,
        establishedYear: data?.established_year,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching park by name:', error);
      return null;
    }
  },

  /**
   * Get state information from multiple parks (comma-separated)
   * @param {string} parkNames - Comma-separated park names
   * @returns {Promise<string|null>} State name or null
   */
  async getStateFromParks(parkNames) {
    try {
      if (!parkNames) return null;

      // Split and get first park
      const firstPark = parkNames?.split(',')?.[0]?.trim();
      const parkData = await this.getParkByName(firstPark);
      
      return parkData?.state || null;
    } catch (error) {
      console.error('Error getting state from parks:', error);
      return null;
    }
  },

  /**
   * Search parks by state
   * @param {string} state - State name or abbreviation
   * @returns {Promise<Array>} Array of parks in the state
   */
  async searchParksByState(state) {
    try {
      if (!state) return [];

      const { data, error } = await supabase?.from('state_parks')?.select('*')?.ilike('state', `%${state}%`)?.order('name');

      if (error) throw error;

      return data?.map(park => ({
        id: park?.id,
        name: park?.name,
        state: park?.state,
        parkType: park?.park_type,
        description: park?.description,
        websiteUrl: park?.website_url,
        totalAcreage: park?.total_acreage,
        establishedYear: park?.established_year
      })) || [];
    } catch (error) {
      console.error('Error searching parks by state:', error);
      return [];
    }
  },

  /**
   * Get all states with parks
   * @returns {Promise<Array<string>>} Array of unique state names
   */
  async getAllStates() {
    try {
      const { data, error } = await supabase?.from('state_parks')?.select('state')?.order('state');

      if (error) throw error;

      // Get unique states
      const uniqueStates = [...new Set(data?.map(item => item.state))];
      return uniqueStates;
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  }
};

export default stateParkService;